import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiClient, setOn401Handler } from '@/shared/api/client';
import { isQflyError } from '@/shared/api/problem';
import type { CurrentUser } from '@/shared/api/types';
import { env } from '@/env';
import { Roles, type Role } from './roles';
import { AuthContext, type AuthContextValue, type LoginMethod } from './authContext';
import { isNativeLoginEnabled } from './nativeLoginFlag';

// Cognito identity-provider name configured on the pool
// (infra/terraform/modules/cognito: entra_provider_name = "EntraID").
// Passing `identity_provider` skips the hosted-UI chooser and goes straight
// to SSO.
//
// A `COGNITO_IDP = 'COGNITO'` constant used to live here for a
// `login('credentials')` branch that redirected to the Cognito hosted-UI
// native-login form. Removed 2026-08: the Qfly-hosted native-login cutover
// moved that form onto the login page itself (`LoginPage.tsx`), which talks
// to `POST /auth/login` directly and never calls `login()`. `LoginMethod` is
// now `'sso'`-only (`authContext.ts`), so this can't be reintroduced silently.
const ENTRA_IDP = 'EntraID';

/**
 * Builds the Cognito hosted-UI authorization URL for the SSO path.
 * Token delivery is via HttpOnly cookie — the frontend never sees the JWT.
 *
 * ``identityProvider`` is omitted when native login is off, keeping the URL
 * byte-identical to today (the pool has only the Entra provider, so it
 * auto-federates). When native login is on the pool also offers COGNITO (for
 * the admin-provisioned native accounts), so we pass EntraID explicitly to
 * skip the now-two-option hosted-UI chooser page.
 */
const buildHostedUiUrl = (identityProvider?: string): string => {
  const params = new URLSearchParams({
    client_id: env.cognito.clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: env.cognito.redirectUri,
  });
  if (identityProvider) params.set('identity_provider', identityProvider);
  return `${env.cognito.hostedUi}?${params.toString()}`;
};

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  // `_method` (prefixed per this project's argsIgnorePattern) keeps the
  // public AuthContextValue['login'] signature stable — callers still pass
  // 'sso' explicitly — even though LoginMethod is now 'sso'-only and this
  // SSO-only implementation has nothing left to branch on.
  const login = useCallback((_method: LoginMethod = 'sso'): void => {
    // Only hint the provider when native login is on (else keep today's exact
    // SSO URL so the critical prod SSO path is unchanged and can't break on a
    // provider-name mismatch).
    const identityProvider = isNativeLoginEnabled() ? ENTRA_IDP : undefined;
    window.location.assign(buildHostedUiUrl(identityProvider));
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore — local state is cleared regardless.
    }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // Install the 401 handler for the shared axios client.
  useEffect(() => {
    setOn401Handler(() => {
      setUser(null);
      setStatus('unauthenticated');
    });
    return () => setOn401Handler(null);
  }, []);

  // Fetch session on mount. `/auth/me` returns 401 when no session.
  // Skip while the OAuth exchange is in progress (/auth/callback or /callback).
  // At that point no session cookie exists yet — the 401 would trip the Axios
  // interceptor into firing /auth/refresh (also no cookie), producing a visible
  // 401 in the network tab and briefly flipping status to 'unauthenticated'.
  // window.location.replace after a successful exchange causes a full reload,
  // so AuthProvider remounts on the destination route and fires /auth/me then.
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, '');
    const isCallbackRoute = path === '/auth/callback' || path === '/callback';
    if (isCallbackRoute) return;
    let cancelled = false;
    const run = async (): Promise<void> => {
      try {
        // The wire shape is snake_case (backend `ResolvedIdentityOut`),
        // but components read camelCase. Cast through `unknown` so we can
        // re-shape without fighting the type checker.
        const response = await apiClient.get<unknown>('/auth/me');
        if (!cancelled) {
          const raw = response.data as {
            user_id?: string;
            id?: string;
            email?: string | null;
            display_name?: string | null;
            displayName?: string;
            roles?: readonly string[];
            regions?: readonly string[];
            is_admin?: boolean;
          };
          const id = raw.id ?? raw.user_id ?? '';
          const email = raw.email ?? '';
          const displayName =
            raw.displayName ?? raw.display_name ?? email ?? id ?? '';
          const user: CurrentUser = {
            id,
            email,
            displayName,
            roles: raw.roles ?? [],
            regions: raw.regions ?? [],
            user_id: raw.user_id,
            display_name: raw.display_name,
            is_admin: raw.is_admin,
          };
          setUser(user);
          setStatus('authenticated');
        }
      } catch (err) {
        if (cancelled) return;
        if (isQflyError(err) && err.status === 401) {
          setStatus('unauthenticated');
          return;
        }
        setStatus('unauthenticated');
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const roles = (user?.roles ?? []) as readonly Role[];
    const hasRole = (role: Role): boolean => roles.includes(role);
    return {
      user,
      status,
      roles,
      regions: user?.regions ?? [],
      isAdmin: hasRole(Roles.Admin),
      hasRole,
      login,
      logout,
    };
  }, [user, status, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
